import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
  Chip,
  Button,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';

import {
  RadioButtonChecked as MultipleChoiceIcon,
  TextFields as TextIcon,
  Star as RatingIcon,
  CheckBox as CheckboxIcon,
  ArrowDropDown as DropdownIcon,
  DateRange as DateIcon,
  CloudUpload as FileIcon,
  GridOn as MatrixIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import Screenshot from './Screenshot';
import ScreenshotGallery from './ScreenshotGallery';
import mcq from '../../assets/images/mcq.webp';
import rating from '../../assets/images/rating.webp';
import matrix from '../../assets/images/matrix.webp';
import short_answer from '../../assets/images/short_answer.webp';

const QuestionTypes = () => {
  const [selectedTab, setSelectedTab] = useState(0);

  const questionTypes = [
    {
      id: 'multiple-choice',
      name: 'Multiple Choice',
      icon: <MultipleChoiceIcon color="primary" />,
      description: 'Allow respondents to select one or multiple options from a predefined list.',
      useCases: ['Product preferences', 'Demographic questions', 'Yes/No questions', 'Category selection'],
      bestFor: 'When you need structured, quantifiable responses',
      example: {
        question: 'Which of the following best describes your role?',
        options: ['Manager', 'Developer', 'Designer', 'Marketing', 'Other']
      },
      settings: [
        'Single vs. multiple selection',
        'Randomize option order',
        'Add "Other" option with text field',
        'Set minimum/maximum selections'
      ]
    },
    {
      id: 'text-input',
      name: 'Text Input',
      icon: <TextIcon color="primary" />,
      description: 'Collect open-ended responses with short text, long text, or paragraph fields.',
      useCases: ['Feedback collection', 'Name/email capture', 'Detailed explanations', 'Comments'],
      bestFor: 'When you need qualitative, detailed responses',
      example: {
        question: 'What improvements would you like to see in our product?',
        placeholder: 'Please share your thoughts...'
      },
      settings: [
        'Single line vs. multi-line text',
        'Character limits',
        'Input validation (email, phone, etc.)',
        'Placeholder text'
      ]
    },
    {
      id: 'rating-scale',
      name: 'Rating Scale',
      icon: <RatingIcon color="primary" />,
      description: 'Measure satisfaction, agreement, or intensity using scales and ratings.',
      useCases: ['Satisfaction surveys', 'Product ratings', 'Agreement scales', 'Performance evaluation'],
      bestFor: 'Measuring attitudes, opinions, and satisfaction levels',
      example: {
        question: 'How satisfied are you with our customer service?',
        scale: '1 (Very Dissatisfied) to 5 (Very Satisfied)'
      },
      settings: [
        'Scale range (1-5, 1-10, etc.)',
        'Star ratings vs. numeric',
        'Custom labels for scale points',
        'Neutral option inclusion'
      ]
    },
    {
      id: 'yes-no',
      name: 'Yes/No',
      icon: <CheckboxIcon color="primary" />,
      description: 'Simple binary choice questions for clear yes/no responses.',
      useCases: ['Qualification questions', 'Consent forms', 'Simple preferences', 'Screening questions'],
      bestFor: 'Quick decisions and filtering respondents',
      example: {
        question: 'Have you used our product before?',
        options: ['Yes', 'No']
      },
      settings: [
        'Custom labels (Yes/No, True/False, etc.)',
        'Default selection',
        'Required vs. optional'
      ]
    },
    {
      id: 'dropdown',
      name: 'Dropdown',
      icon: <DropdownIcon color="primary" />,
      description: 'Space-saving selection from a dropdown list, ideal for long option lists.',
      useCases: ['Country/state selection', 'Age ranges', 'Industry categories', 'Long option lists'],
      bestFor: 'When you have many options but limited screen space',
      example: {
        question: 'What is your country of residence?',
        placeholder: 'Select a country...'
      },
      settings: [
        'Searchable dropdown',
        'Default placeholder text',
        'Alphabetical sorting',
        'Custom option grouping'
      ]
    },
    {
      id: 'date-time',
      name: 'Date/Time',
      icon: <DateIcon color="primary" />,
      description: 'Collect specific dates, times, or date ranges with built-in calendar pickers.',
      useCases: ['Event scheduling', 'Birth dates', 'Appointment booking', 'Timeline questions'],
      bestFor: 'Collecting precise temporal information',
      example: {
        question: 'When would you prefer to schedule your appointment?',
        format: 'MM/DD/YYYY'
      },
      settings: [
        'Date only vs. date and time',
        'Date format preferences',
        'Min/max date restrictions',
        'Default date selection'
      ]
    },
    {
      id: 'file-upload',
      name: 'File Upload',
      icon: <FileIcon color="primary" />,
      description: 'Allow respondents to upload files, images, or documents.',
      useCases: ['Resume collection', 'Photo submissions', 'Document uploads', 'Portfolio reviews'],
      bestFor: 'Collecting visual or document-based responses',
      example: {
        question: 'Please upload your resume or CV',
        formats: 'PDF, DOC, DOCX (Max 5MB)'
      },
      settings: [
        'Allowed file types',
        'Maximum file size',
        'Multiple file uploads',
        'Image preview options'
      ]
    },
    {
      id: 'matrix',
      name: 'Matrix/Grid',
      icon: <MatrixIcon color="primary" />,
      description: 'Ask multiple related questions with the same set of answer choices in a grid format.',
      useCases: ['Feature ratings', 'Multiple evaluations', 'Comparison questions', 'Batch ratings'],
      bestFor: 'Efficiently collecting ratings on multiple items',
      example: {
        question: 'Please rate the following features:',
        items: ['Ease of use', 'Performance', 'Design', 'Support'],
        scale: 'Poor to Excellent'
      },
      settings: [
        'Single vs. multiple selection per row',
        'Randomize row order',
        'Required rows',
        'Custom column headers'
      ]
    }
  ];

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const currentType = questionTypes[selectedTab];

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700, }}>
        Question Types
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
        Explore all the question types available in SurveySlack and learn when to use each one 
        for maximum effectiveness.
      </Typography>

      {/* Overview */}
      <Alert severity="info" sx={{ mb: 4 }}>
        <Typography variant="body2">
          <strong>Choosing the right question type</strong> is crucial for collecting quality data. 
          Each type serves different purposes and provides different insights.
        </Typography>
      </Alert>

      {/* Question Type Tabs */}
      <Card sx={{ mb: 4 }}>
        <Tabs
          value={selectedTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          {questionTypes.map((type, index) => (
            <Tab
              key={type.id}
              label={type.name}
              icon={type.icon}
              iconPosition="start"
              sx={{ minHeight: 60 }}
            />
          ))}
        </Tabs>

        <CardContent sx={{ p: 4 }}>
          {/* Current Type Details */}
          <Box sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              {currentType.icon}
              <Typography variant="h4" sx={{ ml: 1, fontWeight: 500 }}>
                {currentType.name}
              </Typography>
            </Box>
            <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem' }}>
              {currentType.description}
            </Typography>
            <Chip 
              label={`Best for: ${currentType.bestFor}`} 
              color="primary" 
              variant="outlined" 
              sx={{ mb: 3 }}
            />
          </Box>

          <Grid container spacing={4}>
            {/* Example */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, backgroundColor: 'grey.50', border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 500 }}>
                  Example
                </Typography>
                <Typography variant="body1" gutterBottom sx={{ fontWeight: 500 }}>
                  {currentType.example.question}
                </Typography>
                {currentType.example.options && (
                  <List dense>
                    {currentType.example.options.map((option, index) => (
                      <ListItem key={index} sx={{ py: 0.5, pl: 0 }}>
                        <ListItemIcon sx={{ minWidth: 30 }}>
                          <CheckIcon fontSize="small" color="primary" />
                        </ListItemIcon>
                        <ListItemText primary={option} />
                      </ListItem>
                    ))}
                  </List>
                )}
                {currentType.example.placeholder && (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    Placeholder: {currentType.example.placeholder}
                  </Typography>
                )}
                {currentType.example.scale && (
                  <Typography variant="body2" color="text.secondary">
                    Scale: {currentType.example.scale}
                  </Typography>
                )}
                {currentType.example.format && (
                  <Typography variant="body2" color="text.secondary">
                    Format: {currentType.example.format}
                  </Typography>
                )}
                {currentType.example.formats && (
                  <Typography variant="body2" color="text.secondary">
                    Accepted formats: {currentType.example.formats}
                  </Typography>
                )}
                {currentType.example.items && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Items to rate:
                    </Typography>
                    {currentType.example.items.map((item, index) => (
                      <Chip key={index} label={item} size="small" sx={{ mr: 1, mb: 1 }} />
                    ))}
                  </Box>
                )}
              </Paper>
            </Grid>

            {/* Use Cases */}
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 500 }}>
                Common Use Cases
              </Typography>
              <List>
                {currentType.useCases.map((useCase, index) => (
                  <ListItem key={index} sx={{ py: 0.5, pl: 0 }}>
                    <ListItemIcon>
                      <CheckIcon color="success" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary={useCase} />
                  </ListItem>
                ))}
              </List>

              <Typography variant="h6" gutterBottom sx={{ fontWeight: 500, mt: 3 }}>
                Configuration Options
              </Typography>
              <List>
                {currentType.settings.map((setting, index) => (
                  <ListItem key={index} sx={{ py: 0.5, pl: 0 }}>
                    <ListItemIcon>
                      <InfoIcon color="info" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary={setting} />
                  </ListItem>
                ))}
              </List>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Question Type Examples */}
      <Box sx={{ mb: 6 }}>
        <ScreenshotGallery
          title="Question Type Examples"
          columns={2}
          screenshots={[
            {
              src: mcq,
              alt: "Multiple Choice Question",
              caption: "Multiple choice question with single selection"
            },
            {
              src: short_answer,
              alt: "Text Input Question",
              caption: "Text input question for open-ended responses"
            },
            {
              src: rating,
              alt: "Rating Scale Question",
              caption: "5-star rating scale for satisfaction surveys"
            },
            {
              src: matrix,
              alt: "Matrix Question",
              caption: "Matrix question for rating multiple items"
            }
          ]}
        />
      </Box>

      {/* Quick Reference Guide */}
      <Box sx={{ mb: 6 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 500, mb: 3 }}>
          Quick Reference Guide
        </Typography>
        <Typography variant="body1" paragraph color="text.secondary">
          Use this guide to quickly choose the right question type for your needs:
        </Typography>
        
        {[
          {
            scenario: 'I want to collect structured, quantifiable data',
            recommended: ['Multiple Choice', 'Rating Scale', 'Yes/No', 'Dropdown'],
            color: 'primary'
          },
          {
            scenario: 'I need detailed, qualitative feedback',
            recommended: ['Text Input', 'File Upload'],
            color: 'success'
          },
          {
            scenario: 'I want to save space with many options',
            recommended: ['Dropdown', 'Matrix/Grid'],
            color: 'info'
          },
          {
            scenario: 'I need to collect specific data formats',
            recommended: ['Date/Time', 'File Upload'],
            color: 'warning'
          }
        ].map((guide, index) => (
          <Accordion key={index} sx={{ mb: 1 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                {guide.scenario}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {guide.recommended.map((type, typeIndex) => (
                  <Chip 
                    key={typeIndex} 
                    label={type} 
                    color={guide.color} 
                    variant="outlined" 
                  />
                ))}
              </Box>
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>

      {/* Best Practices */}
      <Box sx={{ mb: 6 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 500, mb: 3 }}>
          Question Design Best Practices
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Alert severity="success">
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 500 }}>
                Do's
              </Typography>
              <List dense>
                <ListItem sx={{ py: 0 }}>
                  <ListItemText primary="Keep questions clear and concise" />
                </ListItem>
                <ListItem sx={{ py: 0 }}>
                  <ListItemText primary="Use appropriate question types for your data" />
                </ListItem>
                <ListItem sx={{ py: 0 }}>
                  <ListItemText primary="Test questions with a small group first" />
                </ListItem>
                <ListItem sx={{ py: 0 }}>
                  <ListItemText primary="Provide clear instructions when needed" />
                </ListItem>
              </List>
            </Alert>
          </Grid>
          <Grid item xs={12} md={6}>
            <Alert severity="error">
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 500 }}>
                Don'ts
              </Typography>
              <List dense>
                <ListItem sx={{ py: 0 }}>
                  <ListItemText primary="Don't use leading or biased questions" />
                </ListItem>
                <ListItem sx={{ py: 0 }}>
                  <ListItemText primary="Avoid double-barreled questions" />
                </ListItem>
                <ListItem sx={{ py: 0 }}>
                  <ListItemText primary="Don't make every question required" />
                </ListItem>
                <ListItem sx={{ py: 0 }}>
                  <ListItemText primary="Avoid overly complex matrix questions" />
                </ListItem>
              </List>
            </Alert>
          </Grid>
        </Grid>
      </Box>

      {/* Next Steps */}
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 500 }}>
          Ready to build your survey?
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Now that you understand question types, learn about advanced features.
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Button variant="contained" href="/app/documentations/creating-surveys/logic-branching">
            Logic & Branching
          </Button>
          <Button variant="outlined" href="/app/documentations/creating-surveys/survey-builder">
            Back to Survey Builder
          </Button>
        </Box>
      </Box>

      {/* Bottom navigation CTA (Previous / Next) */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
              <Button  color="inherit"  variant="contained" href="/app/documentations/building-surveys/survey-builder-basics">
                Previous
              </Button>
              <Button variant="contained" href="/app/documentations/publishing-sharing/publishing-your-survey">
                Next
              </Button>
            </Box>
    </Box>
  );
};

export default QuestionTypes;